"use client";

import { Close, Button, Badge } from "@oreme/ui";
import { decimalToSexagesimal } from "geolib";
import { useSetAtom } from "jotai";
import { Circle, Triangle, Hexagon, MapPin, CirclePlus } from "lucide-react";
import { markerPopupOffset, RPopup } from "maplibre-react-components";
import { useLocale, useTranslations } from "next-intl";
import React, { type ReactElement } from "react";
import { SitesSiteMarker, ObservatoryDrawer } from "@/components";
import { selectedSiteAtom } from "@/store/sites";
import { SiteTypeEnum, type Site, Observatory } from "@/types";

interface SitesSitePopupProps {
  site: Site;
  observatory?: Observatory;
}

function SitesSitePopup({
  site,
  observatory,
}: Readonly<SitesSitePopupProps>): ReactElement {
  const t = useTranslations("sites.SitePopup");
  const locale = useLocale();
  const setSelectedSite = useSetAtom(selectedSiteAtom);

  const latitude = Number(site.latitude);
  const longitude = Number(site.longitude);
  const timeserie = site.stat?.timeserie ? site.stat?.timeserie : 0;

  let icon;
  switch (site.type) {
    case SiteTypeEnum.ZONE:
      icon = <Hexagon className="shrink-0" size={16} />;
      break;
    case SiteTypeEnum.STATION:
      icon = <Triangle className="shrink-0" size={16} />;
      break;
    case SiteTypeEnum.POINT:
    default:
      icon = <Circle className="shrink-0" size={16} />;
      break;
  }

  const handleClose = (): void => {
    setSelectedSite(null);
  };

  return (
    <RPopup
      className="flex flex-col gap-1 max-w-52 p-0 m-0 rounded-md bg-background"
      latitude={latitude}
      longitude={longitude}
      offset={markerPopupOffset}
    >
      <header
        id="tour-2"
        className="flex flex-row gap-2 items-center justify-between p-2 border-b border-border"
      >
        <h4 className="font-semibold text-sm truncate" title={site.name}>
          {site.name}
        </h4>
        <Button
          size="icon-xs"
          onClick={handleClose}
          variant="ghost"
          className="border-0"
        >
          <Close title={t("close")} />
          <span className="sr-only">{t("close")}</span>
        </Button>
      </header>
      <div className="flex flex-col gap-2 p-2">
        {site.description ? (
          <p className="text-xs leading-tight">{site.description}</p>
        ) : null}
        <dl className="flex flex-col gap-1">
          <dt className="hidden">{t("site")}</dt>
          <dd className="flex flex-row gap-2 items-center text-xs">
            {icon}
            {t(`${site.type}.description`)}
          </dd>
          <dt className="hidden">{t("location")}</dt>
          <dd className="flex flex-row gap-2 items-center text-xs">
            <MapPin size={16} />
            {site.region ? `${site.region},` : null} {site.country}
          </dd>
        </dl>

        {timeserie ? (
          <dl className="grid grid-cols-2 gap-0.5 text-xs border border-border rounded p-1 bg-accent-background">
            <dt>{t("timeserie", { count: timeserie })}</dt>
            <dd className="text-right truncate">
              <Badge variant="secondary">{timeserie}</Badge>
            </dd>
          </dl>
        ) : null}

        <dl className="grid grid-cols-2 gap-0.5 text-xs border border-border rounded p-1 bg-accent-background">
          {site.latitude ? (
            <>
              <dt>{t("latitude")}</dt>
              <dd className="text-right truncate">
                {decimalToSexagesimal(site.latitude)}
              </dd>
            </>
          ) : null}
          {site.longitude ? (
            <>
              <dt>{t("longitude")}</dt>
              <dd className="text-right truncate">
                {decimalToSexagesimal(site.longitude)}
              </dd>
            </>
          ) : null}
          {site.altitude ? (
            <>
              <dt>{t("altitude")}</dt>
              <dd className="text-right truncate">
                {site.altitude.toFixed(2)} m
              </dd>
            </>
          ) : null}
          {site.depth ? (
            <>
              <dt>{t("depth")}</dt>
              <dd className="text-right truncate">{site.depth.toFixed(2)} m</dd>
            </>
          ) : null}
        </dl>

        {site.observatoryId ? (
          <div className="relative">
            <Button size="xs" className="w-full justify-between grow-0!">
              <span className="min-w-0 truncate">
                {site.observatoryName[locale]}
              </span>
              <CirclePlus size={16} />
            </Button>
            {observatory ? (
              <ObservatoryDrawer observatory={observatory} />
            ) : null}
          </div>
        ) : null}
      </div>

      <SitesSiteMarker site={site} />
    </RPopup>
  );
}

export { SitesSitePopup, type SitesSitePopupProps };
